import { useMemo, useState, useEffect, useRef } from 'react'
import { Card } from '../primitives'
import { useAuthPost } from '../../hooks/useAxios'
import { useAuth } from '../../contexts/AuthContext'
import { useOutletContext } from "react-router-dom";
import ServiceIcons from "../../assets/service-icons";
import UnregisteredSubscription from './subscription-types/UnregisteredSubscription';
import CanceledSubscription from './subscription-types/CanceledSubscription';
import ActiveSubscription from './subscription-types/ActiveSubscription';
import workspaceStyles from '../Workspace.module.css';
import { IoIosSearch } from 'react-icons/io';
import { useTranslation } from '../../hooks/useTranslation';

export default function WorkspaceSubscriptions() {

    const { url } = useAuth();
    const { clientData, clientDataLoading, mobile } = useOutletContext();
    const [searchFilter, setSearchFilter] = useState('');

    const [subscriptions, setSubscriptions] = useState([]);
    const [filter, setFilter] = useState('all');
    const t = useTranslation();

    useEffect(() => {
        const combinedSubscriptions = [];
        if (clientDataLoading || !clientData || !clientData.subscriptions) {
            setSubscriptions(combinedSubscriptions);
            return;
        }

        for (let i = 0; i < clientData.subscriptions.length; i++) {
            const sub = clientData.subscriptions[i];
            sub.subType = sub.workspace ? 'active' : 'unregistered';
            combinedSubscriptions.push(sub);
        }

        Object.entries(clientData.workspaces).forEach(([workspaceId, workspace]) => {
            if (workspace?.unsubbedSubscriptionItem) {
                const sub = workspace.unsubbedSubscriptionItem;
                sub.subType = 'canceled';
                combinedSubscriptions.push(sub);
            }
        });
        setSubscriptions(combinedSubscriptions.sort((a, b) => a.id - b.id));
    }, [clientData, clientDataLoading]);

    const filteredSubscriptions = useMemo(() => {
        return subscriptions.filter(subscription => {
            if (!searchFilter && filter === 'all') return true;
            let pass = false;
            if (filter === 'all' || subscription.subType === filter) pass = true;
            if (subscription.subType === 'unregistered') return pass;
            const name = clientData?.workspaces[subscription.workspace]?.workspaceName;
            if (!name) return pass;
            return pass && name.toLowerCase().includes(searchFilter.toLowerCase());
        });
    }, [subscriptions, searchFilter, clientData?.workspaces, filter]);

    return (
        <Card className='margin-bottom'>
            <Card.Header>
                <h2>{t('Workspace Subscriptions')}</h2>
            </Card.Header>
            <Card.Body>
                <div className='row-flex space-between align-center'>
                    <div className="workspace-subscription-search-bar">
                        <IoIosSearch className="workspace-subscription-search-bar-icon" />
                        <input 
                            className="workspace-subscription-search-bar-input"
                            placeholder={`${mobile ? t('Search') : t('Search by name')}`}
                            onChange={(e) => setSearchFilter(e.target.value)}
                            type="text"
                        />
                    </div>
                    <div className="workspace-subscription-filter">
                        <select defaultValue="all" className="workspace-subscription-filterSelect" onChange={(e) => setFilter(e.target.value)}>
                            <option value="all" className="workspace-subscription-filterOption">{t('All')}</option>
                            <option value="active" className="workspace-subscription-filterOption">{t('Active')}</option>
                            <option value="canceled" className="workspace-subscription-filterOption">{t('Canceled')}</option>
                            <option value="unregistered" className="workspace-subscription-filterOption">{t('Unregistered')}</option>
                        </select>
                    </div>
                </div>
                {filteredSubscriptions && filteredSubscriptions.length > 0 ? filteredSubscriptions.map((subscription, index) => (
                    <div key={index}>
                        <Subscription subscription={subscription} url={url} workspace={clientData?.workspaces[subscription.workspace] || null} />
                    </div>
                )) : <h3 className="text-center">{t('No subscriptions.')}</h3>}
            </Card.Body>
        </Card>
    )
}

function Subscription({ index, subscription, url, workspace }) {

    const { post, loading } = useAuthPost(true, false);

    const serviceIcon = useMemo(() => {
        if (workspace?.icon?.image_102) {
            return workspace.icon.image_102;
        } else if (workspace?.icon && workspace.workspaceType === 2) {
            return `https://cdn.discordapp.com/icons/${subscription.workspace}/${workspace.icon}`;
        } else if (workspace?.icon && workspace.workspaceType === 1) {
            return workspace.icon;
        } else if (workspace?.icon && workspace.workspaceType === 3) {
            return workspace.icon;
        } else {
            switch (workspace?.workspaceType) {
                case 0:
                    return ServiceIcons["slack.png"];
                case 1:
                    return ServiceIcons["teams.png"];
                case 2:
                    return ServiceIcons["discord.png"];
                default:
                    return null;
            }
        }
    }, [workspace]);

    const props = {
        url,
        subscription,
        workspace,
        workspaceId: subscription.workspace,
        serviceIcon,
        post,
        loading,
        totalCost: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
            ((subscription.cost || 0) + 
             (Array.isArray(subscription.addons) ? 
                subscription.addons.reduce((sum, addon) => 
                    (addon && addon.enabled && typeof addon.cost === 'number') ? 
                        sum + addon.cost : sum, 0) : 0
             )) / 100
        )
    }

    if (subscription.subType === 'unregistered') {
        return <UnregisteredSubscription {...props} />;
    }

    if (subscription.subType === 'canceled') {
        return <CanceledSubscription {...props} />;
    }

    if (subscription.subType === 'active') {
        return <ActiveSubscription {...props} />;
    }
}